import { useState, useRef } from "react";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, FileArrowUp, CheckCircle } from "@phosphor-icons/react";
import api, { API, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const EmployeeImportDialog = ({ open, onOpenChange, onImported }) => {
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setPreview(null); setResult(null); };
  const clearForNewFile = (f) => { setFile(f); setPreview(null); setResult(null); };

  const downloadTemplate = () => {
    const token = localStorage.getItem("pf_token");
    fetch(`${API}/employees/import/template`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "employee_import_template.csv"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Could not download template"));
  };

  const downloadExcel = () => {
    const token = localStorage.getItem("pf_token");
    fetch(`${API}/employees/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "employees.xlsx"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Could not download employees"));
  };

  const runPreview = async () => {
    if (!file) { toast.error("Please choose a CSV or Excel file"); return; }
    setPreviewing(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/employees/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPreviewing(false);
    }
  };

  const upload = async () => {
    if (!file) { toast.error("Please choose a CSV or Excel file"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/employees/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setPreview(null);
      toast.success(`Imported: ${data.created} added, ${data.updated} updated`);
      onImported();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-lg border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Bulk Import Employees</DialogTitle>
          <DialogDescription className="text-[#9CA3AF]">
            Onboard your whole company from a CSV or Excel file in one go.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border border-[#E2E5EA] bg-[#F4F5F7] p-4 text-sm text-[#9CA3AF]">
            Upload a <span className="text-[#0F172A]">CSV or Excel (.xlsx)</span> file to onboard your whole company at once.
            Existing employees (matched by <span className="text-[#0F172A]">employee number</span>) are updated; new ones are created.
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              data-testid="download-template-btn"
              onClick={downloadTemplate}
              className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-red-500"
            >
              <DownloadSimple size={16} className="mr-1.5" /> CSV Template
            </Button>
            <Button
              variant="outline"
              data-testid="export-excel-btn"
              onClick={downloadExcel}
              className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-emerald-400"
            >
              <DownloadSimple size={16} className="mr-1.5" /> Export Excel
            </Button>
          </div>

          <div
            onClick={() => inputRef.current?.click()}
            className="flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-[#E2E5EA] bg-[#F4F5F7] py-8 transition-colors duration-200 hover:border-red-500/40"
          >
            <FileArrowUp size={32} className="text-[#C7CDD6]" />
            <p className="mt-2 text-sm text-[#0F172A]">{file ? file.name : "Click to choose a CSV or Excel file"}</p>
            <p className="text-xs text-[#6B7280]">.csv or .xlsx</p>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              data-testid="import-file-input"
              className="hidden"
              onChange={(e) => clearForNewFile(e.target.files?.[0] || null)}
            />
          </div>

          {preview && !result && (
            <div data-testid="import-preview" className="rounded-md border border-[#E2E5EA] bg-[#FFFFFF]">
              <div className="flex flex-wrap items-center gap-2 border-b border-[#E2E5EA] px-4 py-2.5 text-sm">
                <span className="font-semibold text-[#0F172A]">Preview</span>
                <span className="rounded bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600">{preview.create_count} new</span>
                <span className="rounded bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-600">{preview.update_count} update</span>
                {preview.error_count > 0 && (
                  <span className="rounded bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-600">{preview.error_count} error</span>
                )}
                <span className="ml-auto text-xs text-[#6B7280]">{preview.row_count} rows</span>
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-[#F4F5F7] text-[#6B7280]">
                    <tr>
                      <th className="px-3 py-2 font-medium">Emp No</th>
                      <th className="px-3 py-2 font-medium">Name</th>
                      <th className="px-3 py-2 font-medium">Action</th>
                      <th className="px-3 py-2 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((r, i) => (
                      <tr key={i} data-testid={`import-preview-row-${i}`} className="border-t border-[#EEF1F5] align-top">
                        <td className="px-3 py-2 text-[#0F172A]">{r.employee_no || "—"}</td>
                        <td className="px-3 py-2 text-[#0F172A]">{r.full_name || "—"}</td>
                        <td className="px-3 py-2">
                          <span className={
                            r.action === "create" ? "font-medium text-emerald-600"
                            : r.action === "update" ? "font-medium text-blue-600"
                            : "font-medium text-red-600"
                          }>{r.action}</span>
                        </td>
                        <td className="px-3 py-2 text-[#6B7280]">
                          {r.action === "error" && <span className="text-red-500">{r.message}</span>}
                          {r.action === "create" && "New employee"}
                          {r.action === "update" && (
                            r.changes?.length ? (
                              <ul className="space-y-0.5">
                                {r.changes.map((c, j) => (
                                  <li key={j}>
                                    <span className="text-[#0F172A]">{c.field}</span>: <span className="text-red-400 line-through">{String(c.old) || "∅"}</span> → <span className="text-emerald-600">{String(c.new) || "∅"}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : <span className="italic">no changes</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle size={18} weight="fill" /> Import complete
              </div>
              <p className="mt-2 text-[#9CA3AF]">
                <span className="text-[#0F172A]">{result.created}</span> added ·{" "}
                <span className="text-[#0F172A]">{result.updated}</span> updated
                {result.error_count > 0 && <> · <span className="text-red-400">{result.error_count} skipped</span></>}
              </p>
              {result.errors?.length > 0 && (
                <ul className="mt-2 max-h-24 space-y-0.5 overflow-y-auto text-xs text-red-400">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5]">
            Close
          </Button>
          {!preview ? (
            <Button data-testid="preview-import-btn" onClick={runPreview} disabled={previewing || !file} className="bg-red-500 font-semibold text-white hover:bg-red-600">
              <FileArrowUp size={16} className="mr-1.5" /> {previewing ? "Reading…" : "Preview Changes"}
            </Button>
          ) : (
            <Button data-testid="run-import-btn" onClick={upload} disabled={uploading} className="bg-red-500 font-semibold text-white hover:bg-red-600">
              <UploadSimple size={16} className="mr-1.5" /> {uploading ? "Importing…" : `Confirm Import (${preview.create_count + preview.update_count})`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
